import { FC, useMemo } from "react";
import { observer } from "mobx-react";
import { AIClient } from "@/ai/AIAssistant";
import { Box, Grid, VBox } from "@/components-v2/Box";
import { Button, IconButton } from "@/components-v2/Button";
import { Icon } from "@/components-v2/Icon";
import { Tag } from "@/components-v2/Tag";
import { useDispatch, useLanguage, useRootStore, useTranslation } from "@/stores/RootStoreContext";
import { textFor, openCardDialog, openDashboardDialog } from "./dashboardDialogsDomain";
import {
  PreviewCard,
  PreviewAsSelect,
  PreviewHeadingBar,
  PreviewTabBar,
  PreviewText,
  PreviewVisibilityNote,
  usePreviewData,
} from "./dashboardPreviewComponents";
import {
  cardUsageLabel,
  dashboardStatusLabel,
  dashboardVisibleToLabel,
  DashboardSettingsState,
  selectItem,
  SelectedItem,
} from "./dashboardSettingsDomain";

export const DashboardPreviewPanel: FC<{ state: DashboardSettingsState; selected: SelectedItem }> =
  observer(({ state, selected }) => {
    const { t } = useTranslation();
    const language = useLanguage();
    const dispatch = useDispatch();
    const { http } = useRootStore();
    const aiClient = useMemo(() => new AIClient(http), [http]);
    const overview = state.overview;
    const dashboard =
      selected.kind === "dashboard"
        ? overview?.dashboards.find((d) => d.id === selected.id)
        : undefined;
    const card =
      selected.kind === "card" ? overview?.cards.find((c) => c.id === selected.id) : undefined;
    const cards = dashboard
      ? dashboard.cardIds.flatMap((id) => overview?.cards.find((c) => c.id === id) ?? [])
      : card
        ? [card]
        : [];
    const hasDatePicker = dashboard?.hasDatePicker === true;
    usePreviewData(
      state,
      cards.map((c) => ({ key: c.id, config: c.config })),
      hasDatePicker,
      dashboard?.visibleTo,
    );
    const item = dashboard
      ? {
          title: dashboard.internalName,
          subtitle: dashboardStatusLabel(dashboard),
          editLabel: "enterprise-account.dashboards.edit-dashboard" as const,
          onEdit: openDashboardDialog(state, aiClient, dashboard),
        }
      : card
        ? {
            title: card.internalName,
            subtitle: cardUsageLabel(card),
            editLabel: "enterprise-account.dashboards.edit-card" as const,
            onEdit: openCardDialog(state, aiClient, card),
          }
        : undefined;
    if (!item) return null;
    const heading = dashboard?.heading && textFor(dashboard.heading, language);
    return (
      <VBox bg={"default"} gap={0} grow overflowY={"auto"}>
        <VBox paddingTop={"md"} alignItems={"stretch"}>
          <Box alignItems={"start"} gap={"md"} paddingX={"lg"}>
            <Box alignItems={"center"} gap={"md"} wrap grow className={"min-w-0"}>
              <VBox gap={"xs"} grow overflow={"hidden"} className={"basis-40"}>
                <h4 className={"truncate"}>{item.title}</h4>
                <Box gap={"sm"} alignItems={"center"} textSize={"sm"} whitespace={"nowrap"}>
                  {dashboard ? (
                    <>
                      <Box gap={"xs"} alignItems={"center"}>
                        <Box fg={dashboard.status === "ENABLED" ? "success" : "subtle"}>
                          <Icon name={"dot"} weight={"fill"} />
                        </Box>
                        {t(item.subtitle)}
                      </Box>
                      {state.overview && (
                        <Tag
                          color={"neutral"}
                          compact
                          label={dashboardVisibleToLabel(state.overview, dashboard)}
                        />
                      )}
                    </>
                  ) : (
                    <Box fg={"subtle"}>{t(item.subtitle)}</Box>
                  )}
                </Box>
              </VBox>
              <Box gap={"md"} alignItems={"center"} wrap>
                <PreviewAsSelect state={state} savedVisibleTo={dashboard?.visibleTo} />
                <Button
                  variant={"secondary"}
                  icon={"edit"}
                  label={{ key: item.editLabel }}
                  onClick={item.onEdit}
                />
              </Box>
            </Box>
            <IconButton
              icon={"x"}
              tooltip={{ key: "close" }}
              color={"gray"}
              onClick={() => dispatch(selectItem(state, undefined))}
            />
          </Box>
          <PreviewTabBar
            inset
            title={
              dashboard ? (
                <PreviewText
                  text={textFor(dashboard.title, language)}
                  placeholder={"enterprise-account.dashboards.dashboard-title"}
                />
              ) : (
                t("preview")
              )
            }
          />
        </VBox>
        <VBox bg={"base"} grow alignItems={"stretch"}>
          <VBox container maxWidth={"xl"} padding={"lg"} gap={"lg"} alignItems={"stretch"}>
            {dashboard && (
              <PreviewVisibilityNote
                state={state}
                savedVisibleTo={dashboard.visibleTo}
                visibleTo={dashboard.visibleTo}
                isEnabled={dashboard.status === "ENABLED"}
              />
            )}
            <PreviewHeadingBar
              state={state}
              dateInputId={"dashboardPanelAsOf"}
              heading={heading}
              hasDatePicker={hasDatePicker}
            />
            {card ? (
              <Box justifyContent={"center"} paddingTop={"xl"}>
                <VBox className={"w-[24rem] max-w-full"}>
                  <PreviewCard
                    state={state}
                    heading={card.heading}
                    previewKey={card.id}
                    config={card.config}
                  />
                </VBox>
              </Box>
            ) : cards.length === 0 ? (
              <Box fg={"subtle"}>{t("enterprise-account.dashboards.no-cards")}</Box>
            ) : (
              <Grid cols={2} gap={"lg"}>
                {cards.map((c) => (
                  <PreviewCard
                    key={c.id}
                    state={state}
                    heading={c.heading}
                    previewKey={c.id}
                    config={c.config}
                  />
                ))}
              </Grid>
            )}
          </VBox>
        </VBox>
      </VBox>
    );
  });
